/*
Copyright 2024 New Vector Ltd.

SPDX-License-Identifier: AGPL-3.0-only OR LicenseRef-Element-Commercial
Please see LICENSE in the repository root for full details.
*/

import { useEffect, useLayoutEffect, useRef, useState } from "react";
import { WidgetApiToWidgetAction } from "matrix-widget-api";
import { type IThemeChangeActionRequest } from "matrix-widget-api";

import { getUrlParams } from "./UrlParams";
import { widget } from "./widget";
import {
  readSharedTheme,
  saveSharedTheme,
} from "./utils/sharedThemePreference";

export const THEME_EVENT = "element-theme-change";
import { THEME_STORAGE_KEY } from "./utils/appearancePreference";
export { THEME_STORAGE_KEY } from "./utils/appearancePreference";

export const useTheme = (): void => {
  const [requestedTheme, setRequestedTheme] = useState(() => {
    const requested = getUrlParams().theme;
    if (requested) return requested;
    const shared = !widget && readSharedTheme();
    if (shared) return shared;
    try {
      return localStorage.getItem(THEME_STORAGE_KEY) ?? undefined;
    } catch {
      return undefined;
    }
  });
  useEffect(() => {
    const update = (event: Event): void => {
      const value = (event as CustomEvent).detail;
      if (value === "light" || value === "dark") setRequestedTheme(value);
    };
    const storage = (event: StorageEvent): void => {
      if (
        event.key === THEME_STORAGE_KEY &&
        (event.newValue === "light" || event.newValue === "dark")
      )
        setRequestedTheme(event.newValue);
    };
    const syncShared = (): void => {
      if (widget || document.visibilityState === "hidden") return;
      const value = readSharedTheme();
      if (value)
        setRequestedTheme((previous) =>
          previous === value ? previous : value,
        );
    };
    window.addEventListener(THEME_EVENT, update);
    window.addEventListener("storage", storage);
    window.addEventListener("focus", syncShared);
    document.addEventListener("visibilitychange", syncShared);
    const timer = window.setInterval(syncShared, 1000);
    return () => {
      window.removeEventListener(THEME_EVENT, update);
      window.removeEventListener("storage", storage);
      window.removeEventListener("focus", syncShared);
      document.removeEventListener("visibilitychange", syncShared);
      window.clearInterval(timer);
    };
  }, []);
  const previousTheme = useRef<string | null>(document.body.classList.item(0));

  useEffect(() => {
    if (widget) {
      const onThemeChange = (
        ev: CustomEvent<IThemeChangeActionRequest>,
      ): void => {
        ev.preventDefault();
        if ("name" in ev.detail.data && typeof ev.detail.data.name === "string")
          setRequestedTheme(ev.detail.data.name);
        widget!.api.transport.reply(ev.detail, {});
      };

      widget.lazyActions.on(WidgetApiToWidgetAction.ThemeChange, onThemeChange);
      return (): void => {
        widget!.lazyActions.off(
          WidgetApiToWidgetAction.ThemeChange,
          onThemeChange,
        );
      };
    }
  }, []);

  useLayoutEffect(() => {
    // If no theme has been explicitly requested we default to dark
    const theme = requestedTheme?.includes("light") ? "light" : "dark";
    const themeHighContrast = requestedTheme?.includes("high-contrast")
      ? "-hc"
      : "";
    const themeString = "cpd-theme-" + theme + themeHighContrast;
    // Safari paints its surrounding chrome from the document canvas. Keep
    // its tokens in sync even when the body's theme was already applied.
    document.documentElement.classList.remove(
      "cpd-theme-light",
      "cpd-theme-dark",
      "cpd-theme-light-hc",
      "cpd-theme-dark-hc",
    );
    document.documentElement.classList.add(themeString);
    document.documentElement.style.colorScheme = theme;
    if (themeString !== previousTheme.current) {
      document.body.classList.remove(
        "cpd-theme-light",
        "cpd-theme-dark",
        "cpd-theme-light-hc",
        "cpd-theme-dark-hc",
      );
      document.body.classList.add(themeString);
      previousTheme.current = themeString;
    }
    if (!widget) {
      // Supply a chrome color for browsers honoring theme-color. The iOS call
      // canvas also has an opaque body fallback for Safari's own color sampling.
      const transparent = (value: string): boolean =>
        !value || value === "transparent" || value === "rgba(0, 0, 0, 0)";
      const htmlColor = getComputedStyle(
        document.documentElement,
      ).backgroundColor;
      const bodyColor = getComputedStyle(document.body).backgroundColor;
      let chrome = document.querySelector<HTMLMetaElement>(
        'meta[name="theme-color"]',
      );
      if (!chrome) {
        chrome = document.createElement("meta");
        chrome.name = "theme-color";
        document.head.append(chrome);
      }
      chrome.content = !transparent(htmlColor)
        ? htmlColor
        : !transparent(bodyColor)
          ? bodyColor
          : theme === "light"
            ? "#edf3f8"
            : "#101418";
      saveSharedTheme(theme + (themeHighContrast ? "-high-contrast" : ""));
      try {
        localStorage.setItem(
          THEME_STORAGE_KEY,
          theme + (themeHighContrast ? "-high-contrast" : ""),
        );
      } catch {
        /* Appearance remains usable when browser storage is unavailable. */
      }
    }
    document.body.classList.remove("no-theme");
  }, [previousTheme, requestedTheme]);
};
